import { create } from 'zustand'
import { getStoragePrefix, getLegacyStoragePrefix } from '../org'
import { saveSessionToNative, signOutFromNative } from '../native'

export interface AuthUser {
  userId: string
  email: string
  name: string
  role: 'ADMIN' | 'MANAGER' | 'EMPLOYEE'
  organizationId: string
  plan: string
  mustChangePassword?: boolean
  privacyConsentedAt?: string | null
  hasDevice?: boolean
  employeeNumber?: string | null
  departmentName?: string | null
}

interface Tokens {
  accessToken: string
  refreshToken: string
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
  setAuth: (tokens: Tokens, user: AuthUser) => void
  setTokens: (tokens: Tokens) => void
  clearAuth: () => void
  rehydrate: () => void
}

function key(name: string) {
  return `${getStoragePrefix()}_${name}`
}

const SESSION_KEYS = ['access_token', 'refresh_token', 'user'] as const

/**
 * 회사가 안 붙던 시절의 키를 지금 회사 아래로 옮긴다 (2026-09-09).
 *
 * 이게 없으면 배포하는 순간 **지금 로그인해 있는 사람이 전부 한 번 끊긴다.** 저장된
 * 값은 그대로 있는데 읽는 이름이 달라지기 때문이다.
 *
 * 옛 키는 마지막에 로그인한 회사의 것이고, 그 회사가 곧 지금 `org_code` 다.
 * 그래서 지금 접두사 아래로 옮기는 것이 맞다.
 *
 * ⚠ 회사를 모르면(접두사가 옛것과 같으면) 아무것도 안 한다. 옮길 곳이 없다.
 * ⚠ 새 키가 이미 있으면 덮지 않는다. 그쪽이 더 최근이다.
 */
function migrateLegacyKeys() {
  if (typeof window === 'undefined') return
  const prefix = getStoragePrefix()
  if (prefix === getLegacyStoragePrefix()) return
  try {
    for (const name of SESSION_KEYS) {
      const legacyKey = `${getLegacyStoragePrefix()}_${name}`
      const value = localStorage.getItem(legacyKey)
      if (value === null) continue
      if (localStorage.getItem(`${prefix}_${name}`) === null) {
        localStorage.setItem(`${prefix}_${name}`, value)
      }
      localStorage.removeItem(legacyKey)
    }
  } catch {
    // 저장소가 막힌 브라우저 — 이관을 못 해도 로그인 화면으로 갈 뿐이라 흐름을 막지 않는다
  }
}

function loadFromStorage(): Pick<AuthState, 'accessToken' | 'refreshToken' | 'user'> {
  if (typeof window === 'undefined') return { accessToken: null, refreshToken: null, user: null }
  try {
    migrateLegacyKeys()
    const raw = localStorage.getItem(key('user'))
    return {
      accessToken: localStorage.getItem(key('access_token')),
      refreshToken: localStorage.getItem(key('refresh_token')),
      user: raw ? (JSON.parse(raw) as AuthUser) : null,
    }
  } catch {
    return { accessToken: null, refreshToken: null, user: null }
  }
}

export const useAuth = create<AuthState>((set) => ({
  ...loadFromStorage(),

  setAuth: (tokens, user) => {
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, user })
    localStorage.setItem(key('access_token'), tokens.accessToken)
    localStorage.setItem(key('refresh_token'), tokens.refreshToken)
    localStorage.setItem(key('user'), JSON.stringify(user))
    saveSessionToNative({ ...tokens, user })
  },

  /** refresh 응답에 user 가 없을 때 — 토큰만 교체 */
  setTokens: (tokens) => {
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken })
    localStorage.setItem(key('access_token'), tokens.accessToken)
    localStorage.setItem(key('refresh_token'), tokens.refreshToken)
  },

  clearAuth: () => {
    set({ accessToken: null, refreshToken: null, user: null })
    localStorage.removeItem(key('access_token'))
    localStorage.removeItem(key('refresh_token'))
    localStorage.removeItem(key('user'))
    // biznomi_org_code 는 지우지 않는다 — 로그아웃 후에도 같은 회사의
    // 사번/이메일 로그인 분기가 유지되어야 하므로. 회사 변경은 설정에서만.
    signOutFromNative()
  },

  /**
   * 저장소에서 다시 읽는다 (2026-09-09).
   *
   * 🔴 **접두사에 회사가 들어가면서 순서가 생겼다.** 이 스토어는 모듈이 평가될 때
   *   `loadFromStorage()` 를 한 번 부르는데, `main.tsx` 가 `./router` 를 import 하는
   *   줄이 `adoptOrgCodeFromQuery()` 보다 **먼저** 평가된다. 그래서 `/mtt/?org=<코드>`
   *   로 들어온 첫 방문에서는 읽는 시점에 회사를 아직 모른다.
   *
   *   그 경우 토큰은 어차피 없으니(회사 코드 화면에서 막 넘어온 참이다) 잃는 건
   *   없지만, 회사를 안 뒤 한 번 더 읽어 두면 이관도 그때 돈다.
   */
  rehydrate: () => set(loadFromStorage()),
}))

/** React 밖(API 클라이언트 등)에서 현재 상태를 읽을 때 */
export const authStore = {
  get: () => useAuth.getState(),
}
